import { useEffect, useState } from "react";
import { Copy, Check, Loader2, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

/** The GC's portal still has to be keyed by hand. One screen, field by field, in the order the
 *  portal asks for it, every value one click from the clipboard. */
export default function PortalSheet({ project, appId, onClose }) {
  const [d, setData] = useState(null);
  const [copied, setCopied] = useState("");
  const [editing, setEditing] = useState(false);
  const [labels, setLabels] = useState({});

  useEffect(() => {
    opsApi.portalSheet(project.id, appId)
      .then((res) => {
        setData(res);
        setLabels(Object.fromEntries([...res.header, ...res.summary]
          .map((f) => [f.key, f.gc_label || ""])));
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not build the portal sheet");
        onClose();
      });
  }, [project.id, appId, onClose]);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1200);
    } catch { toast.error("The browser blocked the clipboard — select the value and copy it"); }
  };

  const saveLabels = async () => {
    try {
      await opsApi.savePortalLabels(project.id, labels);
      toast.success("This GC's field names saved");
      setEditing(false);
      const res = await opsApi.portalSheet(project.id, appId);
      setData(res);
      setLabels(Object.fromEntries([...res.header, ...res.summary]
        .map((f) => [f.key, f.gc_label || ""])));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save those names"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="my-6 w-full max-w-3xl rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-portal-sheet">
        {!d ? (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Building the sheet…
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  GC Portal Sheet · {d.number}
                </h3>
                <p className="mt-1 text-xs text-slate-500">{d.note}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm"
                        onClick={() => (editing ? saveLabels() : setEditing(true))}
                        data-testid="ops-portal-labels-edit">
                  {editing ? <><Check className="mr-1.5 h-3.5 w-3.5" /> Save names</>
                    : <><Pencil className="mr-1.5 h-3.5 w-3.5" /> Their field names</>}
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}
                        data-testid="ops-portal-close"><X className="h-4 w-4" /></Button>
              </div>
            </div>

            <table className="mt-4 w-full text-xs" data-testid="ops-portal-fields">
              <tbody className="divide-y divide-slate-100">
                {[...d.header, ...d.summary].map((f) => (
                  <tr key={f.key} data-testid={`ops-portal-field-${f.key}`}>
                    <td className="py-1.5 pr-3 align-top">
                      {editing ? (
                        <Input value={labels[f.key] ?? ""} placeholder={f.label}
                               onChange={(e) => setLabels((p) => ({ ...p,
                                 [f.key]: e.target.value }))}
                               className="h-7 text-xs"
                               data-testid={`ops-portal-label-${f.key}`} />
                      ) : (
                        <>
                          <span className="text-slate-700">{f.gc_label || f.label}</span>
                          {f.gc_label && (
                            <span className="ml-1 text-[10px] text-slate-400">({f.label})</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="w-40 py-1.5 text-right font-medium tabular-nums text-slate-900">
                      {f.value}
                    </td>
                    <td className="w-8 py-1.5 pl-2 text-right">
                      <button onClick={() => copy(f.key, f.copy)}
                              data-testid={`ops-portal-copy-${f.key}`}
                              className="text-slate-400 hover:text-slate-900">
                        {copied === f.key ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                          : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex items-center gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Line items
              </h4>
              <Button variant="outline" size="sm" className="ml-auto"
                      onClick={() => copy("all", d.copy_all_lines)}
                      data-testid="ops-portal-copy-lines">
                {copied === "all" ? <Check className="mr-1.5 h-3.5 w-3.5" />
                  : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                Copy every line (tab separated)
              </Button>
            </div>
            <div className="mt-2 max-h-64 overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>{d.line_header.map((h) => (
                    <th key={h} className={`px-2 py-1.5 ${h === "Description" || h === "Cost Code"
                      ? "text-left" : "text-right"}`}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.lines.map((l) => (
                    <tr key={l.item} data-testid={`ops-portal-line-${l.cost_code}`}>
                      <td className="px-2 py-1 font-mono">{l.cost_code}</td>
                      <td className="px-2 py-1">{l.description}</td>
                      {[l.scheduled_value, l.previous, l.this_period, l.stored,
                        l.completed_and_stored].map((v, i) => (
                        <td key={i} className="px-2 py-1 text-right tabular-nums">
                          {v.toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
